
import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {
    Qualification,
    QualificationId,
    QualificationModule, QualificationProp
} from "../../models/qualifications/Qualification";
import {  ofType, StateObservable } from "redux-observable";
import { switchMap, map, catchError } from "rxjs/operators";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {Nullable} from "../../models/common/Nullable";
import {Identifier } from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {sendUserToPage} from "../common/UserViewSettingsActions";

/**
 * base API path to interact with project roles
 */
function apiPath( id: Nullable<number> = null ): string {
    return APP_CONFIG.baseWeb + "qualifications/"+ ( id !== null ? id : '');
}

/** union of all project role actions types */
export type QualificationActionTypes = FetchQualificationsActionTypes
    | FetchQualificationByIdActionTypes
    | CreateQualificationActionTypes
    | UpdateQualificationActionTypes
    | DeleteQualificationActionTypes
    | RestoreQualificationActionTypes

/**
 * FETCH QUALIFICATIONS BY USER VIEW SETTINGS
 *
 * user view settings will automatically be retrieved from redux store
 */
// TYPESCRIPT REDUX ACTION TYPES
export interface FetchQualificationsAction extends ViewIdentifiable, ReduxAction< typeof FETCH_QUALIFICATIONS > {}
export interface FetchQualificationsSuccessAction extends ViewIdentifiable, ReduxAction<typeof FETCH_QUALIFICATIONS_SUCCESS > {
    payload: ApiReturnType< Qualification[] >
}
export interface FetchQualificationsErrorAction extends ViewIdentifiable, ErrorAction< typeof FETCH_QUALIFICATIONS_ERROR >{}

export type FetchQualificationsActionTypes = FetchQualificationsAction
    | FetchQualificationsErrorAction
    | FetchQualificationsSuccessAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_QUALIFICATIONS = "FETCH_QUALIFICATIONS";
export const FETCH_QUALIFICATIONS_SUCCESS = "FETCH_QUALIFICATIONS_SUCCESS";
export const FETCH_QUALIFICATIONS_ERROR = "FETCH_QUALIFICATIONS_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function fetchQualifications( viewIdentifier: string ) : FetchQualificationsAction {
    return { type: FETCH_QUALIFICATIONS, viewIdentifier };
}
export function fetchQualificationsSuccess(
    viewIdentifier: string,
    payload: ApiReturnType< Qualification[] >
) : FetchQualificationsSuccessAction
{
    return { type: FETCH_QUALIFICATIONS_SUCCESS, viewIdentifier, payload };
}
export function fetchQualificationsError( viewIdentifier: string, error: Error ): FetchQualificationsErrorAction {
    return { type: FETCH_QUALIFICATIONS_ERROR, viewIdentifier, error };
}

// REDUX OBSERVABLE EPIC
export function fetchQualificationsEpic(
    action$: Observable< FetchQualificationsAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_QUALIFICATIONS ),
        switchMap( action => {
            const request = getUserViewSettingsApiRequest(
                apiPath(),
                action.viewIdentifier,
                store$
            );

            return ajax( APP_CONFIG.getRequestObject(request.path) ).pipe(
                map( response => fetchQualificationsSuccess(
                    action.viewIdentifier,
                    response.response as ApiReturnType< Qualification[] >
                ) ),
                catchError(error => of(
                    fetchQualificationsError(action.viewIdentifier, error),
                    eventNotificationError(error, store$, action.viewIdentifier, "fetchQualifications")
                ))
            );

        })
    );
}

/**
 * FETCH SINGLE QUALIFICATION
 */
export interface FetchQualificationByIdAction extends QualificationId, ReduxAction< typeof FETCH_QUALIFICATION_BY_ID> {

}
export interface FetchQualificationByIdSuccessAction extends QualificationProp, ReduxAction< typeof FETCH_QUALIFICATION_BY_ID_SUCCESS> {

}
export interface FetchQualificationByIdErrorAction extends QualificationId, ErrorAction< typeof FETCH_QUALIFICATION_BY_ID_ERROR> {

}
//union of action to fetch by id
export type FetchQualificationByIdActionTypes = FetchQualificationByIdAction
    | FetchQualificationByIdSuccessAction
    | FetchQualificationByIdErrorAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_QUALIFICATION_BY_ID = "FETCH_QUALIFICATION_BY_ID";
export const FETCH_QUALIFICATION_BY_ID_SUCCESS = "FETCH_QUALIFICATION_BY_ID_SUCCESS";
export const FETCH_QUALIFICATION_BY_ID_ERROR = "FETCH_QUALIFICATION_BY_ID_ERROR";

//Redux action accessors function
export function fetchQualificationById( qualificationId: Identifier<number> ): FetchQualificationByIdAction {
    return { type: FETCH_QUALIFICATION_BY_ID, qualificationId };
}
export function fetchQualificationByIdSuccess( qualification: Qualification ): FetchQualificationByIdSuccessAction {
    return { type: FETCH_QUALIFICATION_BY_ID_SUCCESS, qualification };
}
export function fetchQualificationByIdError( error: any, qualificationId: Identifier<number> ): FetchQualificationByIdErrorAction {
    return { type: FETCH_QUALIFICATION_BY_ID_ERROR, error, qualificationId };
}

//redux observable epic as bridge to rxjs
export function fetchQualificationByIdEpic(
    action$: Observable< FetchQualificationByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_QUALIFICATION_BY_ID ),
        switchMap( action => {
            return ajax(
                    APP_CONFIG.getRequestObject( apiPath( action.qualificationId.rawValue ) )
                ).pipe(
                    map( response => fetchQualificationByIdSuccess( response.response as Qualification ) ),
                    catchError( error => of(
                        fetchQualificationByIdError( error, action.qualificationId ),
                        eventNotificationError( error, store$, QualificationModule.identifier, "fetchQualificationById" )
                    ) )
                )
            }
        )
    );
}

/**
 * MUTATING REQUESTS
 */

/**
 * CREATE QUALIFICATION
 */
export interface CreateQualificationAction extends QualificationProp, ReduxAction<typeof CREATE_QUALIFICATION> {

}
export interface CreateQualificationSuccessAction extends QualificationId, ReduxAction<typeof CREATE_QUALIFICATION_SUCCESS> {

}
export interface CreateQualificationErrorAction extends ErrorAction<typeof CREATE_QUALIFICATION_ERROR> {

}
// typescript union type of create actions
export type CreateQualificationActionTypes = CreateQualificationAction
    | CreateQualificationSuccessAction
    | CreateQualificationErrorAction

//redux action constants
export const CREATE_QUALIFICATION = "CREATE_QUALIFICATION";
export const CREATE_QUALIFICATION_SUCCESS = "CREATE_QUALIFICATION_SUCCESS";
export const CREATE_QUALIFICATION_ERROR = "CREATE_QUALIFICATION_ERROR";

//redux action accessors functions
export function createQualification( qualification: Qualification ): CreateQualificationAction {
    return { type: CREATE_QUALIFICATION, qualification };
}
export function createQualificationSuccess( qualificationId: Identifier<number> ): CreateQualificationSuccessAction {
    return { type: CREATE_QUALIFICATION_SUCCESS, qualificationId };
}
export function createQualificationError( error: any ): CreateQualificationErrorAction {
    return { type: CREATE_QUALIFICATION_ERROR, error };
}

//redux observable rxjs bridge
export function createQualificationEpic(
    action$: Observable< CreateQualificationAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_QUALIFICATION ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.post,
                    action.qualification
                )
            ).pipe(
                switchMap( response => of(
                    createQualificationSuccess( ( response.response as QualificationId ).qualificationId ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.qualifications.routes.main.path,
                        QualificationModule.identifier
                    )
                ) ),
                catchError( error => of(
                    createQualificationError( error ),
                    eventNotificationError( error, store$, QualificationModule.identifier, "createQualification" )
                ))
            )
        )
    )
}

/**
 * UPDATE QUALIFICATION
 */
export interface UpdateQualificationAction extends QualificationProp, ReduxAction<typeof UPDATE_QUALIFICATION> {

}
export interface UpdateQualificationSuccessAction extends QualificationId, ReduxAction<typeof UPDATE_QUALIFICATION_SUCCESS> {

}
export interface UpdateQualificationErrorAction extends ErrorAction<typeof UPDATE_QUALIFICATION_ERROR> {

}
// typescript union type of update actions
export type UpdateQualificationActionTypes = UpdateQualificationAction
    | UpdateQualificationSuccessAction
    | UpdateQualificationErrorAction

//redux action constants
export const UPDATE_QUALIFICATION = "UPDATE_QUALIFICATION";
export const UPDATE_QUALIFICATION_SUCCESS = "UPDATE_QUALIFICATION_SUCCESS";
export const UPDATE_QUALIFICATION_ERROR = "UPDATE_QUALIFICATION_ERROR";

//redux action accessors functions
export function updateQualification( qualification: Qualification ): UpdateQualificationAction {
    return { type: UPDATE_QUALIFICATION, qualification };
}
export function updateQualificationSuccess( qualificationId: Identifier<number> ): UpdateQualificationSuccessAction {
    return { type: UPDATE_QUALIFICATION_SUCCESS, qualificationId };
}
export function updateQualificationError( error: any ): UpdateQualificationErrorAction {
    return { type: UPDATE_QUALIFICATION_ERROR, error };
}

//redux observable rxjs bridge
export function updateQualificationEpic(
    action$: Observable< UpdateQualificationAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_QUALIFICATION ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.qualification.qualificationId.rawValue ),
                    RouteMethods.put,
                    action.qualification
                )
            ).pipe(
                switchMap( _ => of(
                    updateQualificationSuccess( action.qualification.qualificationId ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.qualifications.routes.main.path,
                        QualificationModule.identifier
                    )
                ) ),
                catchError( error => of(
                    updateQualificationError( error ),
                    eventNotificationError( error, store$, QualificationModule.identifier, "updateQualification" )
                ))
            )
        )
    )
}

/**
 * DELETE QUALIFICATION
 */
export interface DeleteQualificationAction extends QualificationId, ReduxAction<typeof DELETE_QUALIFICATION> {

}
export interface DeleteQualificationSuccessAction extends QualificationId, ReduxAction<typeof DELETE_QUALIFICATION_SUCCESS> {

}
export interface DeleteQualificationErrorAction extends ErrorAction<typeof DELETE_QUALIFICATION_ERROR> {

}
// typescript union type of delete actions
export type DeleteQualificationActionTypes = DeleteQualificationAction
    | DeleteQualificationSuccessAction
    | DeleteQualificationErrorAction

//redux action constants
export const DELETE_QUALIFICATION = "DELETE_QUALIFICATION";
export const DELETE_QUALIFICATION_SUCCESS = "DELETE_QUALIFICATION_SUCCESS";
export const DELETE_QUALIFICATION_ERROR = "DELETE_QUALIFICATION_ERROR";

//redux action accessors functions
export function deleteQualification( qualificationId: Identifier<number>  ): DeleteQualificationAction {
    return { type: DELETE_QUALIFICATION, qualificationId };
}
export function deleteQualificationSuccess( qualificationId: Identifier<number> ): DeleteQualificationSuccessAction {
    return { type: DELETE_QUALIFICATION_SUCCESS, qualificationId };
}
export function deleteQualificationError( error: any ): DeleteQualificationErrorAction {
    return { type: DELETE_QUALIFICATION_ERROR, error };
}

//redux observable rxjs bridge
export function deleteQualificationEpic(
    action$: Observable< DeleteQualificationAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_QUALIFICATION ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.qualificationId.rawValue ),
                    RouteMethods.delete
                )
            ).pipe(
                switchMap( _ => of(
                    deleteQualificationSuccess( action.qualificationId ),
                    eventNotificationSuccess(),
                    fetchQualificationById( action.qualificationId )
                ) ),
                catchError( error => of(
                    deleteQualificationError( error ),
                    eventNotificationError( error, store$, QualificationModule.identifier, "deleteQualification" )
                ))
            )
        )
    )
}

/**
 * RESTORE QUALIFICATION
 */
export interface RestoreQualificationAction extends QualificationId, ReduxAction<typeof RESTORE_QUALIFICATION> {

}
export interface RestoreQualificationSuccessAction extends QualificationId, ReduxAction<typeof RESTORE_QUALIFICATION_SUCCESS> {

}
export interface RestoreQualificationErrorAction extends ErrorAction<typeof RESTORE_QUALIFICATION_ERROR> {

}
// typescript union type of restore actions
export type RestoreQualificationActionTypes = RestoreQualificationAction
    | RestoreQualificationSuccessAction
    | RestoreQualificationErrorAction

//redux action constants
export const RESTORE_QUALIFICATION = "RESTORE_QUALIFICATION";
export const RESTORE_QUALIFICATION_SUCCESS = "RESTORE_QUALIFICATION_SUCCESS";
export const RESTORE_QUALIFICATION_ERROR = "RESTORE_QUALIFICATION_ERROR";

//redux action accessors functions
export function restoreQualification( qualificationId: Identifier<number>  ): RestoreQualificationAction {
    return { type: RESTORE_QUALIFICATION, qualificationId };
}
export function restoreQualificationSuccess( qualificationId: Identifier<number> ): RestoreQualificationSuccessAction {
    return { type: RESTORE_QUALIFICATION_SUCCESS, qualificationId };
}
export function restoreQualificationError( error: any ): RestoreQualificationErrorAction {
    return { type: RESTORE_QUALIFICATION_ERROR, error };
}

//redux observable rxjs bridge
export function restoreQualificationEpic(
    action$: Observable< RestoreQualificationAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_QUALIFICATION ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.qualificationId.rawValue ),
                    RouteMethods.patch
                )
            ).pipe(
                switchMap( _ => of(
                    restoreQualificationSuccess( action.qualificationId ),
                    eventNotificationSuccess(),
                    fetchQualificationById( action.qualificationId )
                ) ),
                catchError( error => of(
                    restoreQualificationError( error ),
                    eventNotificationError( error, store$, QualificationModule.identifier, "restoreQualification" )
                ))
            )
        )
    )
}